import { observer } from 'mobx-react';
import React from 'react';
import { AttributesEditor, IAttribute } from 'controls/react/attributesEditor';
import { makeAutoObservable } from 'mobx';
import { newGuid } from 'tools/guid';
import classnames from 'classnames';
import { useStore } from 'core/react/useStore';
import { ModelValidator, ValidatableModel } from 'framework/mobx-integration';
import { uniq } from 'lodash';

const b = require('b_').with('component-attributes');

const i18n = require('core/localization/localization').translator({
	INVALID_ATTRIBUTE_KEY: {
		en: 'Allowed characters: \na–z, 0–9, hyphen (-), underscore (_), and period (.).',
		no: 'Tillatte tegn: \na–z, 0–9, bindestrek (-), understrek (_) og punktum (.).',
	},
	RESERVED_ATTRIBUTE_KEY: {
		en: 'Key is reserved, it wont be saved as custom attribute',
		no: 'N\u00F8kkel er reservert, det blir ikke lagret som attributt',
	},
	ATTRIBUTES_HAVE_ERRORS: {
		en: 'One or more attributes have an empty or invalid key',
		no: 'En eller flere attributter har en tom eller ugyldig n\u00F8kkel',
	},
});

function isValidDataAttribute(attributeName: string) {
	// Check if the string is empty
	if (!attributeName || attributeName.length === 0) {
		return false;
	}

	// Data attribute names must:
	// 1. Start with a lowercase letter (a-z)
	// 2. Can contain lowercase letters (a-z), digits (0-9), hyphens (-), dots (.), colons (:), and underscores (_)
	// 3. Cannot contain uppercase letters or other special characters

	const dataAttributeRegex = /^[a-z][a-z0-9-.:_]*$/;

	return dataAttributeRegex.test(attributeName);
}

export class CustomAttributes implements ValidatableModel<CustomAttributes> {
	attributes: ComponentAttribute[] = [];
	allAttributes: ComponentAttribute[] = [];

	validator = new ModelValidator(this);
	reservedKeys: string[];

	constructor() {
		makeAutoObservable(this);

		this.validator.add('attributes', {
			callback: () => !!this.attributes?.every((x) => x.validator.valid),
			reactionExpression: () => !!this.attributes?.every((x) => x.validator.valid),
			message: i18n('ATTRIBUTES_HAVE_ERRORS'),
		});
	}

	add(key?: string, value?: any) {
		const attr = new ComponentAttribute({ key, value }, this.reservedKeys);
		this.attributes.push(attr);
		this.allAttributes.push(attr);
		return this;
	}

	delete(attr: ComponentAttribute) {
		this.attributes = [...(this.attributes ?? [])].filter((v) => v.id != attr.id);
	}

	destroy = () => {
		this.allAttributes.forEach((x) => x.destroy());
		this.validator.destroy();
	};
}

export class ComponentAttribute implements IAttribute, ValidatableModel<ComponentAttribute> {
	key: string;
	value: string;
	id: string;
	originalKey: string;

	validator = new ModelValidator(this);

	destroy = () => {
		this.validator.destroy();
	};

	constructor(data: Partial<ComponentAttribute> = {}, reservedKeys: string[] = []) {
		this.id = newGuid();
		Object.assign(this, data);
		this.originalKey = this.key;
		makeAutoObservable(this);

		this.validator
			.required('key')
			.add('key', {
				callback: () => isValidDataAttribute(this.key),
				message: i18n('INVALID_ATTRIBUTE_KEY'),
			})
			.add('key', {
				callback: () => !reservedKeys.includes(this.key),
				message: i18n('RESERVED_ATTRIBUTE_KEY'),
			});
	}
}

export interface AttributesConfig {
	attributes: CustomAttributes;
}

export type AttributesEditorProps = {
	config: AttributesConfig;
	className?: string;
};

export class AttributesStore {
	constructor(public props: AttributesEditorProps) {
		makeAutoObservable(this);
	}

	onAdd = () => {
		this.props.config.attributes.add();
	};

	onDelete = (val: ComponentAttribute) => {
		this.props.config.attributes.delete(val);
	};

	get attributes() {
		return this.props.config.attributes.attributes;
	}
}

export const ComponentAttributes = observer((props: AttributesEditorProps) => {
	const store = useStore(() => new AttributesStore(props));
	const { className } = props;

	const rootClassName = classnames(b('editor'), className);
	return (
		<AttributesEditor
			value={store.attributes}
			onDelete={store.onDelete}
			onAdd={store.onAdd}
			className={rootClassName}
		/>
	);
});
